import type {
  Aircraft,
  DerivedStats,
  Flight,
  IsoInstant,
  LocalDate,
  LogbookEntry,
  Track,
} from '@/lib/db';

/**
 * The join, in one place.
 *
 * `Flight` is the join point the data model promised — a flight may have a
 * track, a logbook entry, both or neither. Every page that wants more than one
 * of those at a time (the flight list, a flight's own page, an aircraft's
 * history) reads a `FlightSummary` rather than re-deriving the joins itself,
 * so "which entry belongs to this track" is answered once and answered the
 * same way everywhere.
 *
 * Building it is a pure function over four already-fetched arrays. That is
 * deliberate: the join is the part worth testing, and a test for it should not
 * need a database.
 */

/**
 * `'merged'` is a flight whose own entry was folded into another one — two
 * recordings of one flight, added together in the logbook. The track is still
 * here and still worth listing; its hours are just counted on someone else's
 * line now, and saying "No entry" would invite the pilot to write a second one.
 */
export type LogbookStatus = 'none' | 'draft' | 'approved' | 'merged';

export interface FlightSummary {
  /**
   * Stable list key. A flight's own id where there is a flight record, and
   * `track:<trackId>` for a track imported before flights were written (or
   * whose flight was deleted) — those still belong on the list, and a synthetic
   * key keeps them there without inventing a `Flight` row nobody asked for.
   */
  id: string;
  flight?: Flight;
  track?: Track;
  aircraft?: Aircraft;
  entry?: LogbookEntry;
  /**
   * Set when this flight had a logbook entry and a merge absorbed it: the id
   * of the entry its hours now live in. Never set alongside `entry` — one
   * flight's hours are on exactly one logbook line, which is what keeps every
   * total that folds over these summaries from counting a merge twice.
   */
  mergedIntoEntryId?: string;
  /** Local calendar date — the flight's own where there is one, else the track's start in the device zone. */
  date: LocalDate;
  /** Sort key and the time shown: the track's first fix, absent for an entry typed by hand. */
  startTime?: IsoInstant;
  endTime?: IsoInstant;
  registration?: string;
  /** Departure and arrival as a pilot writes them: ICAO where known, else the strip's name. */
  from?: string;
  to?: string;
  /** Never invented: an empty-but-`low` set of stats for a flight with no track behind it. */
  stats: DerivedStats;
}

const NO_STATS: DerivedStats = { confidence: 'low' };

function aerodromeLabel(ref: { icao?: string; name?: string } | undefined): string | undefined {
  return ref?.icao ?? ref?.name;
}

/** `YYYY-MM-DD` in the device's zone. Not `toISOString`, which would be UTC's calendar day. */
function localDateOf(instant: IsoInstant): LocalDate {
  const date = new Date(instant);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export interface FlightSummaryInput {
  flights: readonly Flight[];
  tracks: readonly Track[];
  aircraft: readonly Aircraft[];
  entries: readonly LogbookEntry[];
}

/**
 * Joins flights, tracks, aircraft and logbook entries into one list, newest
 * first.
 *
 * Every flight appears. So does every track with no flight pointing at it —
 * dropping those would hide an imported file from the only page that lists
 * imported files. A flight whose `trackId` points at a track that is gone is
 * kept too, with no track: the logbook entry behind it is still a record.
 */
export function buildFlightSummaries(input: FlightSummaryInput): FlightSummary[] {
  const trackById = new Map(input.tracks.map((track) => [track.id, track]));
  const aircraftById = new Map(input.aircraft.map((record) => [record.id, record]));
  const aircraftByRegistration = new Map(
    input.aircraft.map((record) => [record.registration, record]),
  );
  const entryByFlightId = new Map(input.entries.map((entry) => [entry.flightId, entry]));
  /*
   * The flights whose entry was merged away, pointing at the entry that
   * absorbed it. Read off `mergedFrom` rather than stored on the flight: the
   * merge is a logbook operation and leaves the flights table alone, so the
   * logbook is the only place that knows.
   */
  const mergedIntoByFlightId = new Map<string, LogbookEntry>();
  for (const entry of input.entries) {
    for (const source of entry.mergedFrom ?? []) {
      mergedIntoByFlightId.set(source.flightId, entry);
    }
  }

  const claimedTrackIds = new Set<string>();
  const summaries: FlightSummary[] = [];

  for (const flight of input.flights) {
    const track = flight.trackId === undefined ? undefined : trackById.get(flight.trackId);
    if (track !== undefined) {
      claimedTrackIds.add(track.id);
    }
    const entry = entryByFlightId.get(flight.id);
    const mergedInto = entry === undefined ? mergedIntoByFlightId.get(flight.id) : undefined;
    const registration =
      (flight.aircraftId === undefined ? undefined : aircraftById.get(flight.aircraftId))
        ?.registration ??
      entry?.registration ??
      track?.registration;
    const aircraft =
      (flight.aircraftId === undefined ? undefined : aircraftById.get(flight.aircraftId)) ??
      (registration === undefined ? undefined : aircraftByRegistration.get(registration));

    summaries.push({
      id: flight.id,
      flight,
      ...(track === undefined ? {} : { track }),
      ...(aircraft === undefined ? {} : { aircraft }),
      ...(entry === undefined ? {} : { entry }),
      ...(mergedInto === undefined ? {} : { mergedIntoEntryId: mergedInto.id }),
      date: flight.date,
      ...(track === undefined ? {} : { startTime: track.startTime, endTime: track.endTime }),
      ...(registration === undefined ? {} : { registration }),
      // The logbook entry wins over the track's guess: a pilot who typed a
      // departure over the derived one meant it.
      ...(() => {
        const from = entry?.from ?? aerodromeLabel(flight.departure);
        const to = entry?.to ?? aerodromeLabel(flight.arrival);
        return {
          ...(from === undefined ? {} : { from }),
          ...(to === undefined ? {} : { to }),
        };
      })(),
      stats: flight.derived,
    });
  }

  for (const track of input.tracks) {
    if (claimedTrackIds.has(track.id)) {
      continue;
    }
    const aircraft =
      track.registration === undefined ? undefined : aircraftByRegistration.get(track.registration);
    summaries.push({
      id: `track:${track.id}`,
      track,
      ...(aircraft === undefined ? {} : { aircraft }),
      date: localDateOf(track.startTime),
      startTime: track.startTime,
      endTime: track.endTime,
      ...(track.registration === undefined ? {} : { registration: track.registration }),
      stats: NO_STATS,
    });
  }

  return summaries.sort(compareByWhenDescending);
}

/**
 * Newest first, by the flight's start where one is known and by its calendar
 * date otherwise — a hand-typed entry has no clock, and sorting it as midnight
 * would push it below every tracked flight on the same day for no reason a
 * pilot would recognise. Ties break on id so the order is total and a re-render
 * never reshuffles two flights against each other.
 */
function compareByWhenDescending(a: FlightSummary, b: FlightSummary): number {
  if (a.date !== b.date) {
    return b.date.localeCompare(a.date);
  }
  const aStart = a.startTime ?? '';
  const bStart = b.startTime ?? '';
  if (aStart !== bStart) {
    return bStart.localeCompare(aStart);
  }
  return a.id.localeCompare(b.id);
}

/** `NZAR → NZTG`, or as much of it as is known. `undefined` when neither end is. */
export function routeLabel(summary: FlightSummary): string | undefined {
  if (summary.from === undefined && summary.to === undefined) {
    return undefined;
  }
  return `${summary.from ?? '—'} → ${summary.to ?? '—'}`;
}

export function logbookStatus(entry: LogbookEntry | undefined): LogbookStatus {
  if (entry === undefined) {
    return 'none';
  }
  return entry.isDraft ? 'draft' : 'approved';
}

/**
 * The same question asked of a whole summary, which is what every page
 * actually has: an entry of its own, an entry it was merged into, or nothing.
 */
export function summaryLogbookStatus(summary: FlightSummary): LogbookStatus {
  if (summary.entry !== undefined) {
    return logbookStatus(summary.entry);
  }
  return summary.mergedIntoEntryId === undefined ? 'none' : 'merged';
}

/** The track a summary would lose if it were unlinked. `undefined` for a flight with none. */
export function linkedTrackId(summary: FlightSummary): string | undefined {
  return summary.track?.id;
}

/** Air time in minutes, preferring the pilot's own logbook hours over the track's guess. */
export function airTimeMinutes(summary: FlightSummary): number | undefined {
  const entry = summary.entry;
  if (entry !== undefined && !entry.isDraft) {
    const hours = entry.time.dayHr + entry.time.nightHr;
    if (hours > 0) {
      return hours * 60;
    }
  }
  if (summary.stats.airTimeMin !== undefined) {
    return summary.stats.airTimeMin;
  }
  if (entry === undefined) {
    return undefined;
  }
  const hours = entry.time.dayHr + entry.time.nightHr;
  return hours > 0 ? hours * 60 : undefined;
}
